import * as React from 'react';
import { Authorization } from '../misc/components/Authorization';
import { Query, Permission, QueryCondition, Subject, Visit, Site, BloodGlucoseSessionStatusEnum } from '../misc/api/apiTypes';
import { formatNull, formatDateTime } from '../misc/components/Formatters/Formatters';
import Condition from '../misc/components/Condition';
import uniqBy from 'lodash-es/uniqBy';
import { Link } from 'react-router-dom';
import { useCurrentStudyConfigurationContext } from '../Core/CurrentStudyConfigurationContext';
import { useWebPortalApiClient } from '../misc/api/WebPortalTokenClient';
import { useSecurityContext } from '../Core/SecurityContext';
import { MatchSortHeader } from '../Components/MatchSortHeader';
import { DateMatchSortHeader } from "../Components/DateMatchSortHeader";
import { useQuery } from "../Components/Query";
import { PermissionEnum } from '../misc/api/ApiPermissionEnum';
import { BloodGlucoseSession } from "webportalclient";
import { Column, Table } from "../Components/QueryTable";
import { useReferenceContextProvider } from '../Core/ReferenceContextProvider';

const RenderMenu = (studyId: string, permissions: Array<Permission>, row: BloodGlucoseSession) => {
    return (
        <Condition test={() => row.status === BloodGlucoseSessionStatusEnum.ReceivedData}>
            <Authorization permissionId={PermissionEnum.AccessAllBloodGlucoseTests} studyId={studyId} currentPermissions={permissions}>
                <div className="text-center">
                    <Link to={`/bloodglucose/tests/${row.id}`} type="button" className="btn btn-primary">
                        View Tests
                    </Link>
                </div>
            </Authorization>
        </Condition>
    );
}

export const BloodGlucoseQueryTable = ({ }) => {
    const { study, fieldDisplayText } = useCurrentStudyConfigurationContext();
    const { data, applyFilters } = useQuery();
    const { bloodGlucoseSessionStatuses } = useReferenceContextProvider();
    const client = useWebPortalApiClient();
    const { currentUserPermissions } = useSecurityContext();
    const [siteOptions, setSiteOptions] = React.useState([]);
    const [subjectOptions, setSubjectOptions] = React.useState([]);
    const [visitOptions, setVisitOptions] = React.useState([]);
    const [sessionOptions, setSessionOptions] = React.useState([]);
    const [configTechOptions, setConfigTechOptions] = React.useState([]);
    const [techOptions, setTechOptions] = React.useState([]);
    const [serialOptions, setSerialOptions] = React.useState([]);

    React.useEffect(() => {
        const getSites = async () => {
            const sites = await client.client.getSites("id,number", `studyId eq ${study.id}`, "number");
            const options = sites.value.map(s => ({
                value: s.id,
                label: s.number,
            }));

            setSiteOptions(options);
        };

        const getSubjects = async () => {
            const subjects = await client.client.getSubjects("id,number", `site/studyId eq ${study.id}`, "number");
            const options = subjects.value.map(s => ({
                value: s.id,
                label: s.number,
            }));

            setSubjectOptions(options);
        };

        const getVisits = async () => {
            const visits = await client.client.getBloodGlucoseSessions("visit", `visit/subject/site/studyId eq ${study.id}`, "visit/name", "visit($expand=subject($expand=site))");
            const options = uniqBy(visits.value, o => o.visit.name).map(s => ({
                value: s.visitId,
                label: s.visit.name,
            }));

            setVisitOptions(options);
        };

        const getTechnicians = async () => {
            const techs = await client.client.getBloodGlucoseSessions("name,configurationTechnician,technician,serialNumber", `visit/subject/site/studyId eq ${study.id}`, undefined);

            const sessOptions = uniqBy(techs.value, o => o.name).filter(o => o.name).map(s => ({
                value: s.name,
                label: s.name,
            }));

            setSessionOptions(sessOptions);

            const options = uniqBy(techs.value, o => o.configurationTechnician).filter(o => o.configurationTechnician).map(s => ({
                value: s.configurationTechnician,
                label: s.configurationTechnician,
            }));

            setConfigTechOptions(options);

            const optionsTech = uniqBy(techs.value, o => o.technician).filter(o => o.technician).map(s => ({
                value: s.technician,
                label: s.technician,
            }));

            setTechOptions(optionsTech);

            const serials = uniqBy(techs.value, o => o.serialNumber).filter(o => o.serialNumber).map(s => ({
                value: s.serialNumber,
                label: s.serialNumber,
            }));

            setSerialOptions(serials);
        };

        getSites();
        getSubjects();
        getVisits();
        getTechnicians();
    }, []);

    const clearAllFilters = () => {
        applyFilters([]);
    }

    const formatStatus = (status: string) => {
        return bloodGlucoseSessionStatuses.find(bgs => bgs.id === status)?.name ?? "-";
    }

    return (<>
        <div className="row">
                <div className="col-md-6">
                    <h2 className='text-primary'>Blood Glucose Sessions</h2>
                </div>
            <div className="col-md-6">
                <div className="h2 pull-right">
                    <button className="btn btn-primary" onClick={() => clearAllFilters()}>Clear All Filters</button>
                </div>
            </div>
        </div>
        <div className='row'>
            <div className='col-md-12'>
                <div className='panel panel-default'>
                    <div className='panel-body'>
                        <div className='table-responsive'>
                            <Table<BloodGlucoseSession> data={data}>
                                <Column field="" header={<MatchSortHeader title="Site" field="visit/subject/siteId" options={siteOptions} sortField="visit/subject/site/number" />} formatter={session => session.visit.subject.site.number} />
                                <Column field="" header={<MatchSortHeader title={fieldDisplayText.SubjectId} field="visit/subjectId" options={subjectOptions} sortField="visit/subject/number" />} formatter={session => session.visit.subject.number} />
                                <Column field="" header={<MatchSortHeader title="Visit" field="visitId" options={visitOptions} sortField="visit/name" />} formatter={session => session.visit.name} />
                                <Column field="" header={<MatchSortHeader title="Session" field="name" options={sessionOptions} sortField="name" />} formatter={session => session.name} />
                                <Column field="" header={<MatchSortHeader title="Configuration Technician" field="configurationTechnician" options={configTechOptions} sortField="configurationTechnician" />} formatter={session => formatNull(session.configurationTechnician)} />
                                <Column field="" header={<MatchSortHeader title="Upload Technician" field="technician" options={techOptions} sortField="technician" />} formatter={session => formatNull(session.technician)} />
                                <Column field="" header={<MatchSortHeader title="Serial Number" field="serialNumber" options={serialOptions} sortField="serialNumber" />} formatter={session => session.serialNumber} />
                                <Column field="" header={<DateMatchSortHeader title="Session Configured" field="configurationOccurred" sortField="configurationOccurred" />} formatter={session => formatDateTime(session.configurationOccurred)} />
                                <Column field="" header={<DateMatchSortHeader title="Session Received" field="received" sortField="received" />} formatter={session => formatDateTime(session.received)} />
                                <Column field="" header={<MatchSortHeader title="Status" field="status" options={[]} sortField="status" />} formatter={session => formatStatus(session.status)} />
                                <Column field="" formatter={session => RenderMenu(study.id, currentUserPermissions, session)} />
                            </Table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        </>);
}
